<template>
  <div class="wait-editor">
    <!-- Node Intro -->
    <div class="node-intro">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M15 1H9V3H15V1ZM11 14H13V8H11V14ZM19.03 7.39L20.45 5.97C20.02 5.46 19.55 4.98 19.04 4.56L17.62 5.98C16.07 4.74 14.12 4 12 4C7.03 4 3 8.03 3 13C3 17.97 7.02 22 12 22C16.98 22 21 17.97 21 13C21 10.88 20.26 8.93 19.03 7.39ZM12 20C8.13 20 5 16.87 5 13C5 9.13 8.13 6 12 6C15.87 6 19 9.13 19 13C19 16.87 15.87 20 12 20Z" fill="#6F4999"/>
      </svg>
      <p class="intro-text">
        <span class="intro-bold">Wait</span> will allow the Actions to be executed after a customized amount of time once the Trigger grants the connected actions.
      </p>
    </div>

    <!-- Wait Type Selection -->
    <div class="input-group">
      <div class="input-label">
        <span>Wait Type </span>
        <span class="required">(Required)</span>
      </div>
      <SingleSelector
        v-model="localFormData.mode"
        placeholder="Select wait type"
        :options="waitTypeOptions"
        @change="handleWaitTypeChange"
      />
    </div>

    <!-- Option 1: Wait for a duration -->
    <template v-if="localFormData.mode === 'duration'">
      <div class="input-group">
        <div class="input-label">
          <span>Wait for </span>
          <span class="required">(Required)</span>
        </div>
        <div class="input-wrapper">
          <input
            v-model="localFormData.durationConfig.value"
            type="number"
            min="1"
            class="wait-input"
            placeholder="Enter duration"
            @input="handleInput"
          />
          <button 
            v-if="localFormData.durationConfig.value"
            class="clear-btn" 
            @click="clearDurationValue"
          >
            Clear
          </button>
        </div>
      </div>

      <div class="input-group">
        <div class="input-label">
          <span>Unit </span>
          <span class="required">(Required)</span>
        </div>
        <SingleSelector
          v-model="localFormData.durationConfig.unit"
          placeholder="Select unit"
          :options="durationUnitOptions"
          @change="handleInput"
        />
      </div>
    </template>

    <!-- Option 2: Wait until a specific time point -->
    <template v-if="localFormData.mode === 'time_point'">
      <div class="input-group">
        <div class="input-label">
          <span>Time Point Type </span>
          <span class="required">(Required)</span>
        </div>
        <SingleSelector
          v-model="localFormData.timepointType"
          placeholder="Select time point type"
          :options="timepointTypeOptions"
          @change="handleTimepointTypeChange"
        />
      </div>

      <!-- days_from_now_at_time: wait N days then continue at HH:MM -->
      <template v-if="localFormData.timepointType === 'days_from_now_at_time'">
        <div class="input-group">
          <div class="input-label">
            <span>Days from now </span>
            <span class="required">(Required)</span>
          </div>
          <div class="input-wrapper">
            <input
              v-model="localFormData.daysFromNow"
              type="number"
              min="0"
              class="wait-input"
              placeholder="0 = today, 1 = tomorrow, ..."
              @input="handleInput"
            />
          </div>
        </div>

        <div class="input-group">
          <div class="input-label">
            <span>Continue at </span>
            <span class="required">(Required)</span>
          </div>
          <div class="time-input-row">
            <input
              v-model="localFormData.hour"
              type="number"
              min="1"
              max="12"
              class="time-input"
              placeholder="HH"
              @input="handleInput"
            />
            <span class="time-separator">:</span>
            <input
              v-model="localFormData.minute"
              type="number"
              min="0"
              max="59"
              class="time-input"
              placeholder="MM"
              @input="handleInput"
            />
            <SingleSelector
              v-model="localFormData.ampm"
              placeholder="AM"
              :options="ampmOptions"
              class="ampm-dropdown"
              @change="handleInput"
            />
          </div>
        </div>
      </template>

      <!-- weekday_at_time_with_week_offset: until (Weekday) at HH:MM with week offset -->
      <template v-if="localFormData.timepointType === 'weekday_at_time_with_week_offset'">
        <div class="input-group">
          <div class="input-label">
            <span>Weekday </span>
            <span class="required">(Required)</span>
          </div>
          <SingleSelector
            v-model="localFormData.weekday"
            placeholder="Select weekday"
            :options="weekdayOptions"
            @change="handleInput"
          />
        </div>

        <div class="input-group">
          <div class="input-label">
            <span>Time </span>
            <span class="required">(Required)</span>
          </div>
          <div class="time-input-row">
            <input
              v-model="localFormData.hour"
              type="number"
              min="1"
              max="12"
              class="time-input"
              placeholder="HH"
              @input="handleInput"
            />
            <span class="time-separator">:</span>
            <input
              v-model="localFormData.minute"
              type="number"
              min="0"
              max="59"
              class="time-input"
              placeholder="MM"
              @input="handleInput"
            />
            <SingleSelector
              v-model="localFormData.ampm"
              placeholder="AM"
              :options="ampmOptions"
              class="ampm-dropdown"
              @change="handleInput"
            />
          </div>
        </div>

        <div class="input-group">
          <div class="input-label">
            <span>Week Offset </span>
            <span class="required">(Required)</span>
          </div>
          <div class="input-wrapper">
            <input
              v-model="localFormData.weekOffset"
              type="number"
              min="0"
              class="wait-input"
              placeholder="0 = this week, 1 = next week, ..."
              @input="handleInput"
            />
          </div>
        </div>
      </template>

      <!-- month_boundary: last_day — no extra inputs needed -->
      <template v-if="localFormData.timepointType === 'last_day_month'">
      </template>

      <!-- month_boundary: first_day — no extra inputs needed -->
      <template v-if="localFormData.timepointType === 'first_day_month'">
      </template>
    </template>

    <!-- Preview Info Box -->
    <div v-if="previewMessage" class="preview-info-box">
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M9 5H11V7H9V5ZM9 9H11V15H9V9ZM10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM10 18C5.59 18 2 14.41 2 10C2 5.59 5.59 2 10 2C14.41 2 18 5.59 18 10C18 14.41 14.41 18 10 18Z" fill="#004879"/>
      </svg>
      <p class="preview-text">{{ previewMessage }}</p>
    </div>

    <!-- Save Configuration Button -->
    <SaveConfigure
      :disabled="!isValid"
      @save-configuration="handleSave"
    />

    <!-- Remove Block Button -->
    <button class="remove-block-btn" @click="handleRemoveBlock">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M4 12.6667C4 13.4 4.6 14 5.33333 14H10.6667C11.4 14 12 13.4 12 12.6667V4.66667H4V12.6667ZM12.6667 2.66667H10.3333L9.66667 2H6.33333L5.66667 2.66667H3.33333V4H12.6667V2.66667Z" fill="#D10000"/>
      </svg>
      <span>Remove Block</span>
    </button>
  </div>
</template>

<script>
import SaveConfigure from '@/components/workflow/SaveConfigure.vue';
import SingleSelector from '@/NewLibrary/SingleSelector.vue';

export default {
  name: 'LogicWaitEditor',
  components: {
    SaveConfigure,
    SingleSelector
  },
  props: {
    data: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['save-configuration', 'remove-block'],
  data() {
    const savedData = this.data?.logicCardData || {};

    // Derive UI display fields from timePointConfig (backend format)
    let timepointType = 'days_from_now_at_time';
    let daysFromNow = 0;
    let hour = '';
    let minute = '';
    let ampm = 'AM';
    let weekday = 'Mon';
    let weekOffset = 0;

    if (savedData.timePointConfig) {
      const tpc = savedData.timePointConfig;
      if (tpc.type === 'month_boundary') {
        timepointType = tpc.boundary === 'last_day' ? 'last_day_month' : 'first_day_month';
      } else {
        timepointType = tpc.type;
      }
      if (tpc.type === 'days_from_now_at_time') {
        daysFromNow = tpc.daysFromNow ?? 0;
        if (tpc.time24h) {
          const parts = tpc.time24h.split(':');
          const h24 = parseInt(parts[0]);
          const m = parts[1] || '00';
          ampm = h24 >= 12 ? 'PM' : 'AM';
          hour = String(h24 % 12 || 12);
          minute = m;
        }
      } else if (tpc.type === 'weekday_at_time_with_week_offset') {
        weekday = tpc.weekday || 'Mon';
        weekOffset = tpc.weekOffset ?? 0;
        if (tpc.time24h) {
          const parts = tpc.time24h.split(':');
          const h24 = parseInt(parts[0]);
          const m = parts[1] || '00';
          ampm = h24 >= 12 ? 'PM' : 'AM';
          hour = String(h24 % 12 || 12);
          minute = m;
        }
      }
    }

    return {
      localFormData: {
        // mode matches backend WaitLogicConfig.mode
        mode: savedData.mode || 'duration',

        // durationConfig matches backend WaitDurationConfig
        durationConfig: {
          value: savedData.durationConfig?.value ?? '',
          unit: savedData.durationConfig?.unit ?? 'hours',
        },

        // UI display helpers for time_point (derived from / converted to timePointConfig)
        timepointType,
        daysFromNow,
        hour,
        minute,
        ampm,
        weekday,
        weekOffset,
      },
      
      waitTypeOptions: [
        { label: 'Wait for a duration', value: 'duration' },
        { label: 'Wait until a specific time point', value: 'time_point' }
      ],
      
      durationUnitOptions: [
        { label: 'Minutes', value: 'minutes' },
        { label: 'Hours', value: 'hours' },
        { label: 'Days', value: 'days' }
      ],
      
      timepointTypeOptions: [
        { label: 'Wait N days then continue at HH:MM', value: 'days_from_now_at_time' },
        { label: 'Until (Weekday) at HH:MM with week offset', value: 'weekday_at_time_with_week_offset' },
        { label: 'Last day of the month', value: 'last_day_month' },
        { label: 'First day of the month', value: 'first_day_month' }
      ],
      
      ampmOptions: [
        { label: 'AM', value: 'AM' },
        { label: 'PM', value: 'PM' }
      ],
      
      weekdayOptions: [
        { label: 'Monday', value: 'Mon' },
        { label: 'Tuesday', value: 'Tue' },
        { label: 'Wednesday', value: 'Wed' },
        { label: 'Thursday', value: 'Thu' },
        { label: 'Friday', value: 'Fri' },
        { label: 'Saturday', value: 'Sat' },
        { label: 'Sunday', value: 'Sun' }
      ]
    };
  },
  computed: {
    isValid() {
      const f = this.localFormData;
      if (f.mode === 'duration') {
        return f.durationConfig.value !== '' && Number(f.durationConfig.value) > 0 && f.durationConfig.unit;
      } else if (f.mode === 'time_point') {
        const hasTime = f.hour && f.minute !== '' && f.ampm;
        if (f.timepointType === 'days_from_now_at_time') {
          return f.daysFromNow !== '' && Number(f.daysFromNow) >= 0 && hasTime;
        } else if (f.timepointType === 'weekday_at_time_with_week_offset') {
          return f.weekday && f.weekOffset !== '' && Number(f.weekOffset) >= 0 && hasTime;
        } else if (f.timepointType === 'last_day_month' || f.timepointType === 'first_day_month') {
          return true;
        }
      }
      return false;
    },
    
    previewMessage() {
      if (!this.isValid) return '';
      
      const now = new Date();
      let targetDate;
      
      if (this.localFormData.mode === 'duration') {
        targetDate = this.calculateDurationTarget(now);
      } else if (this.localFormData.mode === 'time_point') {
        targetDate = this.calculateTimepointTarget(now);
      }
      
      if (!targetDate) return '';
      
      const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const weekday = weekdays[targetDate.getDay()];
      const month = String(targetDate.getMonth() + 1).padStart(2, '0');
      const day = String(targetDate.getDate()).padStart(2, '0');
      const year = targetDate.getFullYear();
      const hours = targetDate.getHours();
      const minutes = String(targetDate.getMinutes()).padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12;
      
      return `If the previous step finishes now, the next step will wait until ${month}/${day}/${year} (${weekday}), ${displayHours}:${minutes} ${ampm}`;
    }
  },
  methods: {
    handleInput() {
      // Trigger preview update
    },
    
    handleWaitTypeChange() {
      // Reset fields when switching wait type
      this.handleInput();
    },
    
    handleTimepointTypeChange() {
      // Reset time-specific fields when switching timepoint type
      this.handleInput();
    },
    
    clearDurationValue() {
      this.localFormData.durationConfig.value = '';
    },
    
    calculateDurationTarget(now) {
      const value = parseInt(this.localFormData.durationConfig.value);
      if (!value || value <= 0) return null;

      const target = new Date(now);

      switch (this.localFormData.durationConfig.unit) {
        case 'minutes':
          target.setMinutes(target.getMinutes() + value);
          break;
        case 'hours':
          target.setHours(target.getHours() + value);
          break;
        case 'days':
          target.setDate(target.getDate() + value);
          break;
      }
      
      return target;
    },
    
    calculateTimepointTarget(now) {
      const f = this.localFormData;
      const hour = parseInt(f.hour);
      const minute = parseInt(f.minute);

      if (f.timepointType === 'last_day_month' || f.timepointType === 'first_day_month') {
        return null; // no preview for month boundary
      }

      if (!hour || minute === '' || !f.ampm) return null;

      // Convert to 24-hour format
      let hour24 = hour;
      if (f.ampm === 'PM' && hour !== 12) hour24 = hour + 12;
      else if (f.ampm === 'AM' && hour === 12) hour24 = 0;

      const target = new Date(now);

      switch (f.timepointType) {
        case 'days_from_now_at_time': {
          const days = Number(f.daysFromNow);
          if (days < 0) return null;
          target.setDate(target.getDate() + days);
          target.setHours(hour24, minute, 0, 0);
          if (days === 0 && target <= now) {
            target.setDate(target.getDate() + 1);
          }
          break;
        }

        case 'weekday_at_time_with_week_offset': {
          const weekOffset = Number(f.weekOffset);
          if (weekOffset < 0) return null;
          const weekdayMap = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 0 };
          const targetWeekday = weekdayMap[f.weekday];
          const currentWeekday = now.getDay();
          let daysUntilTarget = targetWeekday - currentWeekday;
          if (daysUntilTarget <= 0) daysUntilTarget += 7;
          daysUntilTarget += weekOffset * 7;
          target.setDate(target.getDate() + daysUntilTarget);
          target.setHours(hour24, minute, 0, 0);
          break;
        }
      }

      return target;
    },

    toTime24h(hour, minute, ampm) {
      let h = Number(hour);
      const m = String(Number(minute)).padStart(2, '0');
      if (ampm === 'PM' && h !== 12) h += 12;
      else if (ampm === 'AM' && h === 12) h = 0;
      return `${String(h).padStart(2, '0')}:${m}`;
    },

    buildTimePointConfig() {
      const f = this.localFormData;
      const time24h = this.toTime24h(f.hour, f.minute, f.ampm);
      if (f.timepointType === 'days_from_now_at_time') {
        return { type: 'days_from_now_at_time', daysFromNow: Number(f.daysFromNow), time24h };
      } else if (f.timepointType === 'weekday_at_time_with_week_offset') {
        return { type: 'weekday_at_time_with_week_offset', weekday: f.weekday, weekOffset: Number(f.weekOffset), time24h };
      } else if (f.timepointType === 'last_day_month') {
        return { type: 'month_boundary', boundary: 'last_day' };
      } else if (f.timepointType === 'first_day_month') {
        return { type: 'month_boundary', boundary: 'first_day' };
      }
    },
    
    handleSave() {
      if (!this.isValid) return;
      const f = this.localFormData;
      const logicCardData = { mode: f.mode };
      if (f.mode === 'duration') {
        logicCardData.durationConfig = {
          value: Number(f.durationConfig.value),
          unit: f.durationConfig.unit
        };
      } else if (f.mode === 'time_point') {
        logicCardData.timePointConfig = this.buildTimePointConfig();
      }
      this.$emit('save-configuration', {
        logicCardType: 'wait',
        logicCardConfigured: true,
        logicCardData
      });
    },
    
    handleRemoveBlock() {
      this.$emit('remove-block');
    }
  }
};
</script>

<style scoped>
.wait-editor {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}

.node-intro {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  justify-content: center;
  padding: 8px 12px;
  background: #E1D3F1;
  border-radius: 5px;
  width: 100%;
}

.intro-text {
  flex: 1;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 300;
  line-height: normal;
  color: #6F4999;
  margin: 0;
}

.intro-bold {
  font-weight: 600;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

.input-label {
  display: flex;
  gap: 4px;
  align-items: center;
  height: 24px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #4A4A4A;
}

.required {
  color: #D10000;
}

.input-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 40px;
  padding: 8px 12px;
  background: #FFFFFF;
  border: 1px solid #C9C9C9;
  border-radius: 4px;
}

.wait-input {
  flex: 1;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 300;
  line-height: 18px;
  color: #333;
  border: none;
  outline: none;
  background: transparent;
}

.wait-input::placeholder {
  color: #999;
}

.wait-input::-webkit-inner-spin-button,
.wait-input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  appearance: none;
  margin: 0;
}

.wait-input[type=number] {
  -moz-appearance: textfield;
  appearance: textfield;
}

/* Time input row */
.time-input-row {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.time-input {
  width: 60px;
  height: 40px;
  padding: 8px 12px;
  background: #FFFFFF;
  border: 1px solid #C9C9C9;
  border-radius: 4px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 300;
  line-height: 18px;
  color: #333;
  text-align: center;
  outline: none;
}

.time-input::placeholder {
  color: #999;
}

.time-input::-webkit-inner-spin-button,
.time-input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  appearance: none;
  margin: 0;
}

.time-input[type=number] {
  -moz-appearance: textfield;
  appearance: textfield;
}

.time-input:focus {
  border-color: #004879;
}

.time-separator {
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 500;
  color: #333;
}

.ampm-dropdown {
  width: 80px;
  flex-shrink: 0;
}

/* Preview info box */
.preview-info-box {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  padding: 12px;
  background: #E3F2FD;
  border: 1px solid #90CAF9;
  border-radius: 4px;
  width: 100%;
}

.preview-text {
  flex: 1;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #004879;
  margin: 0;
}

.clear-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 4px 10px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #1E4670;
  text-decoration: underline;
  text-decoration-skip-ink: none;
  text-underline-position: from-font;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: opacity 0.2s;
}

.clear-btn:hover {
  opacity: 0.8;
}

.remove-block-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 32px;
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #D10000;
  background: #FFFFFF;
  border: 1px solid #D10000;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.remove-block-btn:hover {
  background: #F63B3B;
  color: #FFFFFF;
  border-color: #F63B3B;
}

.remove-block-btn:hover svg path {
  fill: #FFFFFF;
}

.remove-block-btn:active {
  transform: translateY(1px);
}

/* SingleSelector styles */
:deep(.new-libary-single-selector .el-select__wrapper) {
  height: 40px;
  border: 1px solid #C9C9C9 !important;
  box-shadow: none !important;
  border-radius: 4px;
}

:deep(.new-libary-single-selector .el-select__wrapper:hover) {
  border-color: #C9C9C9 !important;
}

:deep(.new-libary-single-selector .el-select__wrapper.is-focused) {
  border-color: #004879 !important;
}

:deep(.new-libary-single-selector .el-select__placeholder) {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 300;
  color: #999;
}

:deep(.new-libary-single-selector .el-select__selected-item) {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 300;
  color: #333;
}

:deep(.new-libary-single-selector .el-select__caret) {
  color: #374957;
}
</style>
